/**
 * @file mixin-types-utils.ts
 *
 * Shared type primitives and composition utility for the as-app mixin chain.
 * The *MixinMembers interfaces describing each mixin's public surface live in
 * the corresponding mixin file (single source of truth); they are re-exported
 * here for backward compatibility.
 */

/**
 * Base constructor type for class expression mixins. Includes optional
 * connectedCallback / disconnectedCallback so the mixin chain can call
 * super.connectedCallback?.() without a TypeScript error.
 */
export type Constructor<T = HTMLElement> = new (...args: any[]) => T & {
  connectedCallback?(): void | Promise<void>;
  disconnectedCallback?(): void;
};

/** Mixin function shape: accepts any base class and returns a constructor. */
type MixinFn = (base: any) => Constructor;

/** Recursively threads return types of mixin functions to compose the final type. */
type PipelineResult<Base, Fns extends readonly MixinFn[]> =
  Fns extends readonly []
    ? Base
    : Fns extends readonly [(base: any) => infer R extends Constructor, ...infer Rest extends readonly MixinFn[]]
      ? PipelineResult<R, Rest>
      : never;

/**
 * Applies mixins left-to-right (inner → outer). TypeScript infers the full
 * composed type via the recursive PipelineResult, preserving all mixin members.
 */
export function composeMixins<
  T extends typeof HTMLElement,
  const Fns extends readonly MixinFn[]
>(base: T, ...mixins: Fns): PipelineResult<T, Fns> {
  return mixins.reduce<Constructor>(
    (acc, m) => m(acc),
    base as any
  ) as PipelineResult<T, Fns>;
}

// Re-exported for backward compatibility; canonical definitions live in each mixin file.
export type { LocalizationMixinMembers } from './localization-mixin.ts';
export type { VersionMixinMembers } from './version-mixin.ts';
export type { InitQueueMixinMembers } from './init-queue-mixin.ts';
export type { ModalMixinMembers } from './modal-mixin.ts';
export type { ErrorMixinMembers } from './error-mixin.ts';
export type { HistoryMixinMembers } from './history-mixin.ts';
export type { ProjectMixinMembers } from './project-mixin.ts';
export type { MeshMixinMembers } from './mesh-mixin.ts';
export type { FileDropMixinMembers } from './file-drop-mixin.ts';
export type { BusyMixinMembers } from './busy-mixin.ts';
export type { GrabCADMixinMembers } from './grabcad-mixin.ts';
